// 證據包驗證（plan/vms-web-v0.md §5.8 EXP-04）：瀏覽器端解 ZIP、逐檔 SHA-256 比對 manifest、用公開金鑰驗 manifest.sig。內容不上傳。
// 驗章公鑰保留歷史（SPEC #vms-web-540「簽章金鑰代號與歷史」，platform#60）：依 manifest 的 key_fingerprint → key_id 挑；
// 都對不到（2026-10-17 前 dev / test 的包 key_id 是 KMS ARN）就每把都試，驗得過的那把就是。
import { unzipSync } from 'fflate';

export interface EvidenceManifest {
  version: number;
  job_id: string;
  home_id: string;
  exported_by: string;
  reason: string;
  created_at: string;
  generated_at: string;
  key_id?: string;
  /** sha256:<公鑰 SPKI DER 的 SHA-256>（2026-10-17 起的包才有） */
  key_fingerprint?: string;
  algorithm?: string;
  files: { name: string; sha256: string; size: number; device_ref: string; from: string; to: string; source: string; frames: number }[];
}

export interface FileCheck {
  name: string;
  ok: boolean;
  expected: string;
  actual: string;
  missing?: boolean;
}

export interface VerifyResult {
  manifest?: EvidenceManifest;
  files: FileCheck[];
  extra: string[]; // ZIP 裡有、manifest 沒列的
  signature: 'valid' | 'invalid' | 'unsigned' | 'no_key';
  /** 簽章有效時是哪一把（顯示代號、指紋、使用中 / 已停用） */
  key?: PublicKey;
  error?: string;
}

export interface PublicKey {
  key_id: string;
  public_key_pem: string;
  alg?: string;
  /** sha256:<hex>；舊版 plane 沒給 */
  fingerprint?: string;
  status?: 'current' | 'retired';
  first_used_at?: string;
  last_used_at?: string;
}

/** 指紋顯示：sha256: 後的 hex 每 4 碼一組；short = 只取前 16 碼。 */
export function fingerprintText(fp: string | undefined, short = false): string {
  if (!fp) return '';
  const hex = fp.replace(/^sha256:/, '');
  const part = short ? hex.slice(0, 16) : hex;
  return (part.match(/.{1,4}/g) ?? []).join(' ') + (short && hex.length > 16 ? '…' : '');
}

/** 平台清單 → 去重（多個 plane 回同一把時只留一筆，current 優先）。 */
export function uniqueKeys(keys: PublicKey[]): PublicKey[] {
  const out: PublicKey[] = [];
  for (const k of keys) {
    const i = out.findIndex((x) => (k.fingerprint && x.fingerprint === k.fingerprint) || (!k.fingerprint && x.key_id === k.key_id));
    if (i < 0) out.push(k);
    else if (k.status === 'current' && out[i].status !== 'current') out[i] = k;
  }
  return out;
}

/** TS 5.7 起 Uint8Array 帶 buffer 型別參數，WebCrypto 要 ArrayBuffer 版的。 */
const bs = (u: Uint8Array) => u as unknown as BufferSource;

export async function sha256Hex(data: Uint8Array): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', bs(data));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** KMS 的 ECDSA 簽章是 DER（SEQUENCE{INTEGER r, INTEGER s}）；WebCrypto 要 r‖s 各 32 bytes。 */
export function derToRaw(der: Uint8Array, size = 32): Uint8Array {
  let i = 0;
  if (der[i++] !== 0x30) throw new Error('not a DER sequence');
  const len = der[i++];
  if (len & 0x80) i += len & 0x7f;
  const int = () => {
    if (der[i++] !== 0x02) throw new Error('not a DER integer');
    const l = der[i++];
    let v = der.slice(i, i + l);
    i += l;
    while (v.length > size && v[0] === 0) v = v.slice(1);
    if (v.length > size) throw new Error('integer too long');
    const out = new Uint8Array(size);
    out.set(v, size - v.length);
    return out;
  };
  const r = int();
  const s = int();
  const raw = new Uint8Array(size * 2);
  raw.set(r, 0);
  raw.set(s, size);
  return raw;
}

function pemToDer(pem: string): Uint8Array {
  const b64 = pem.replace(/-----[^-]+-----/g, '').replace(/\s+/g, '');
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

export async function verifySignature(manifest: Uint8Array, sigB64: string, pem: string): Promise<boolean> {
  const key = await crypto.subtle.importKey('spki', bs(pemToDer(pem)), { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
  const der = Uint8Array.from(atob(sigB64.trim()), (c) => c.charCodeAt(0));
  let raw: Uint8Array;
  try {
    raw = derToRaw(der);
  } catch {
    return false;
  }
  return crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, key, bs(raw), bs(manifest));
}

/** verifyPackage：ZIP 位元組 → 驗證結果。keys：平台公開的驗章金鑰（依 key_id 挑）。 */
export async function verifyPackage(zip: Uint8Array, keys: PublicKey[]): Promise<VerifyResult> {
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(zip);
  } catch {
    return { files: [], extra: [], signature: 'unsigned', error: 'bad_zip' };
  }
  const mb = entries['manifest.json'];
  if (!mb) return { files: [], extra: Object.keys(entries), signature: 'unsigned', error: 'no_manifest' };
  let manifest: EvidenceManifest;
  try {
    manifest = JSON.parse(new TextDecoder().decode(mb));
  } catch {
    return { files: [], extra: [], signature: 'unsigned', error: 'bad_manifest' };
  }
  const files: FileCheck[] = [];
  for (const f of manifest.files ?? []) {
    const data = entries[f.name];
    if (!data) {
      files.push({ name: f.name, ok: false, expected: f.sha256, actual: '', missing: true });
      continue;
    }
    const actual = await sha256Hex(data);
    files.push({ name: f.name, ok: actual === f.sha256 && data.length === f.size, expected: f.sha256, actual });
  }
  const listed = new Set(['manifest.json', 'manifest.sig', ...(manifest.files ?? []).map((f) => f.name)]);
  const extra = Object.keys(entries).filter((n) => !listed.has(n));
  let signature: VerifyResult['signature'] = 'unsigned';
  let used: PublicKey | undefined;
  const sig = entries['manifest.sig'];
  if (sig) {
    const sigText = new TextDecoder().decode(sig);
    const named =
      (manifest.key_fingerprint ? keys.find((k) => k.fingerprint === manifest.key_fingerprint) : undefined) ??
      (manifest.key_id ? keys.find((k) => k.key_id === manifest.key_id) : undefined);
    const tryKey = async (k: PublicKey) => {
      try {
        return await verifySignature(mb, sigText, k.public_key_pem);
      } catch {
        return false; // 壞掉的 PEM 當成驗不過，換下一把
      }
    };
    if (named) {
      signature = (await tryKey(named)) ? 'valid' : 'invalid';
      if (signature === 'valid') used = named;
    } else if (keys.length === 0) {
      signature = 'no_key';
    } else {
      // 對不到名字：每把都試（舊包的 key_id 是 ARN；換過 key 也一樣）
      for (const k of keys) {
        if (await tryKey(k)) {
          used = k;
          break;
        }
      }
      signature = used ? 'valid' : 'invalid'; // 每把都驗不過：被改過或不是平台簽的
    }
  }
  return { manifest, files, extra, signature, ...(used ? { key: used } : {}) };
}

/** 整體結論：簽章對、每個檔案都對、沒有多出來的檔案。 */
export function verdict(r: VerifyResult): 'pass' | 'fail' | 'unsigned' {
  if (r.error || r.files.some((f) => !f.ok) || r.extra.length > 0 || r.signature === 'invalid' || r.signature === 'no_key') return 'fail';
  return r.signature === 'valid' ? 'pass' : 'unsigned';
}
